import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Copy } from 'lucide-react';
import { authService } from '@/api';
import { useToast } from '@/hooks/useToast';
import { AuthPageShell } from '@/components/ui/AuthPageShell';
import { AppButton } from '@/components/ui/AppButton';
import { Loader } from '@/components/ui/Loader';
import { copyToClipboard } from '@/utils/clipboard';
import { LOGIN_SWITCH_ACCOUNT_QUERY, ROUTES } from '@/routes/paths';

function useQueryToken() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search).get('token') || '', [search]);
}

export function RevealCredentialPage() {
  const token = useQueryToken();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [credentials, setCredentials] = useState(null);
  const [error, setError] = useState('');

  const loginUrl = `${ROUTES.login}?${LOGIN_SWITCH_ACCOUNT_QUERY}=1`;

  useEffect(() => {
    if (!token) {
      setError('Missing link token.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const data = await authService.revealCredential(token);
        if (cancelled) return;
        if (data.redirectToLogin) {
          navigate(loginUrl, { replace: true });
          return;
        }
        setCredentials(data);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'This link is invalid or has expired.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, navigate, loginUrl]);

  const copyField = async (value, label) => {
    const ok = await copyToClipboard(value);
    if (ok) showToast(`${label} copied`, 'success');
    else showToast(`Failed to copy ${label.toLowerCase()}`, 'error');
  };

  const copyAll = async () => {
    if (!credentials) return;
    const text = `Email: ${credentials.email}\nPassword: ${credentials.tempPassword}`;
    const ok = await copyToClipboard(text);
    if (ok) showToast('Email and password copied', 'success');
    else showToast('Failed to copy credentials', 'error');
  };

  const inputClass =
    'w-full px-4 py-3 border border-slate-300 rounded-xl bg-slate-50 text-slate-900 font-mono text-sm';

  return (
    <AuthPageShell
      title="Copy your credentials"
      description="Copy your login email and temporary password, then open the sign-in page."
    >
      {loading ? (
        <Loader label="Loading…" fullHeight={false} />
      ) : error ? (
        <div className="space-y-4">
          <p className="text-sm font-medium text-rose-700">{error}</p>
          <Link to={loginUrl} className="text-sm font-bold text-indigo-600 hover:text-indigo-800">
            Go to sign in
          </Link>
        </div>
      ) : credentials ? (
        <div className="space-y-4">
          <p className="rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
            Use the copy buttons below, then open sign in and paste your credentials.
          </p>
          <div>
            <label className="mb-2 block text-sm font-bold text-slate-700">Login email</label>
            <div className="flex gap-2">
              <input readOnly value={credentials.email} className={inputClass} />
              <AppButton
                type="button"
                variant="secondary"
                onClick={() => void copyField(credentials.email, 'Email')}
                title="Copy email"
              >
                <Copy className="h-4 w-4" />
              </AppButton>
            </div>
          </div>
          <div>
            <label className="mb-2 block text-sm font-bold text-slate-700">
              Temporary password
            </label>
            <div className="flex gap-2">
              <input readOnly value={credentials.tempPassword} className={inputClass} />
              <AppButton
                type="button"
                variant="secondary"
                onClick={() => void copyField(credentials.tempPassword, 'Password')}
                title="Copy password"
              >
                <Copy className="h-4 w-4" />
              </AppButton>
            </div>
          </div>
          <AppButton
            type="button"
            variant="secondary"
            className="w-full"
            onClick={() => void copyAll()}
          >
            Copy email and password
          </AppButton>
          <p className="text-xs font-medium text-slate-500">
            You can return to this page from your email until you sign in and set a new password.
          </p>
          <Link
            to={loginUrl}
            className="flex w-full items-center justify-center rounded-xl bg-indigo-600 px-4 py-3 text-center text-sm font-bold text-white hover:bg-indigo-700"
          >
            Open sign-in page
          </Link>
        </div>
      ) : null}
    </AuthPageShell>
  );
}
